<template>
  <div class="detect-report">
    <div class="report-header">
      <div>
        <h2 class="report-title">{{ displayTitle }}</h2>
        <p v-if="detail.docAuthor" class="report-author">作者 {{ detail.docAuthor }}</p>
        <p class="report-sub">任务编号 {{ detail.taskNo }} · {{ detail.statusLabel }}</p>
      </div>
      <button type="button" class="ghost-btn" :disabled="loading" @click="$emit('refresh')">
        <i class="bi bi-arrow-clockwise" :class="{ spinning: loading }"></i>
        刷新
      </button>
    </div>

    <div v-if="detail.status === '1' || detail.status === '0'" class="report-progress">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: (detail.progress || 0) + '%' }"></div>
      </div>
      <span>检测进行中 {{ detail.progress || 0 }}%</span>
    </div>

    <div v-if="detail.status === '3'" class="report-error">
      <i class="bi bi-exclamation-circle"></i>
      {{ detail.errorMsg || '检测失败' }}
    </div>

    <div v-if="detail.status === '2'" class="summary-card">
      <div class="gauge-wrap">
        <div class="gauge-ring" :style="gaugeStyle">
          <div class="gauge-inner">
            <span class="gauge-value">{{ formatRate(detail.overallAiRate) }}%</span>
            <span class="gauge-label">全文 AI 疑似率</span>
          </div>
        </div>
      </div>
      <div class="summary-metrics">
        <div class="metric metric-ai">
          <span class="metric-value">{{ formatRate(detail.overallAiRate) }}%</span>
          <span class="metric-label">疑似AIGC生成</span>
        </div>
        <div class="metric metric-human">
          <span class="metric-value">{{ formatRate(detail.overallHumanRate) }}%</span>
          <span class="metric-label">人工编写率</span>
        </div>
      </div>
      <div class="summary-stats">
        <span>总字数 {{ detail.wordCount || 0 }}</span>
        <span>检测段落 {{ detail.detectableCount || 0 }}</span>
        <span>跳过段落 {{ detail.skippedCount || 0 }}</span>
        <span>消耗 {{ detail.quotaCost || 0 }} 点</span>
      </div>
      <p class="legend">
        <span class="legend-mark">红色文字</span> 表示AIGC疑似度 ≥ 50%，鼠标悬停可查看具体疑似度。
      </p>
      <p class="disclaimer">本报告为 AI 辅助评估结果，与知网、维普等官方检测结果无直接对应关系，仅供参考。</p>
    </div>

    <div v-if="detail.status === '2' && detail.paragraphs && detail.paragraphs.length" class="document-section">
      <div class="doc-paper">
        <p
            v-for="item in detail.paragraphs"
            :key="item.seqNo"
            class="doc-para"
            :class="{ 'aigc-mark': isFlagged(item) }"
            :data-score="isFlagged(item) ? formatRate(item.aiScore) : null">
          {{ item.sourceText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DetectReport',
  props: {
    detail: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false }
  },
  computed: {
    displayTitle() {
      const d = this.detail || {}
      return d.docTitle || d.sourceFileName || '检测报告'
    },
    gaugeStyle() {
      const rate = Number(this.detail.overallAiRate) || 0
      const deg = Math.min(100, Math.max(0, rate)) * 3.6
      return {
        background: `conic-gradient(#1976d2 0deg ${deg}deg, #e5e7eb ${deg}deg 360deg)`
      }
    }
  },
  methods: {
    formatRate(val) {
      if (val == null || val === '') {
        return '--'
      }
      const num = Number(val)
      return Number.isFinite(num) ? num.toFixed(2) : '--'
    },
    isFlagged(item) {
      if (item.skipped) {
        return false
      }
      return Number(item.aiScore) >= 50
    }
  }
}
</script>

<style scoped>
.detect-report {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.report-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.report-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  color: #111827;
}

.report-author {
  margin: 0 0 4px;
  font-size: 14px;
  color: #374151;
}

.report-sub {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  font-size: 14px;
}

.report-progress {
  padding: 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.progress-bar {
  height: 8px;
  border-radius: 999px;
  background: #e3f2fd;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
  transition: width 0.3s ease;
}

.report-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 14px;
}

.summary-card {
  padding: 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 24px;
  align-items: center;
}

.gauge-wrap {
  display: flex;
  justify-content: center;
}

.gauge-ring {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gauge-inner {
  width: 108px;
  height: 108px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gauge-value {
  font-size: 26px;
  font-weight: 700;
  color: #1976d2;
  line-height: 1.1;
}

.gauge-label {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.summary-metrics {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.metric {
  flex: 1;
  padding: 16px;
  border-radius: 12px;
  color: #fff;
}

.metric-ai {
  background: linear-gradient(135deg, #1976d2, #1565c0);
}

.metric-human {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
}

.metric-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
}

.metric-label {
  font-size: 13px;
  opacity: 0.92;
}

.summary-stats {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 14px;
  color: #6b7280;
}

.disclaimer {
  grid-column: 1 / -1;
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.6;
}

.legend {
  grid-column: 1 / -1;
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #4b5563;
}

.legend-mark {
  color: #dc2626;
  font-weight: 600;
}

.document-section {
  margin-top: 4px;
}

.doc-paper {
  padding: 48px 56px;
  border-radius: 2px;
  background: #fff;
  border: 1px solid #d9dee7;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  font-family: 'SimSun', 'Songti SC', 'NSimSun', serif;
}

.doc-para {
  margin: 0 0 0.75em;
  text-indent: 2em;
  font-size: 16px;
  line-height: 1.85;
  color: #111;
  text-align: justify;
  white-space: pre-wrap;
  word-break: break-word;
}

.doc-para:last-child {
  margin-bottom: 0;
}

.doc-para.aigc-mark {
  position: relative;
  color: #dc2626;
  cursor: help;
}

.doc-para.aigc-mark::after {
  content: '疑似度 ' attr(data-score) '%';
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  padding: 5px 10px;
  background: #fff;
  border: 1px solid #374151;
  border-radius: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: #111;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 20;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.doc-para.aigc-mark:hover::after {
  opacity: 1;
}

.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .summary-card {
    grid-template-columns: 1fr;
  }
}
</style>
